<template>
	<fieldset
		:disabled="disabledByWizard"
		class="input-component-wrapper"
		style="height: unset"
	>
		<div
			:class="generateClassName()"
			ref="container"
		>
			<InfoIcon
				v-if="infoIconObj"
				:infoIconObj="infoIconObj"
			/>
			<label
				v-if="label"
				class="label-root"
			>
				{{ label === '_' ? '\u00A0' : label }}
			</label>

			<div
				:class="`${getCustomCSS}saturn-textbox`"
				:style="getDimensionStyles"
			>
				<div
					v-if="!util.hasKey($slots, 'action') && showIcon && iconLeft"
					class="saturn-icon"
				>
					<i :class="iconStyle" />
				</div>

				<slot
					v-if="isADropdownInput"
					name="dropdown"
				></slot>
				<input
					v-else-if="!multiline"
					v-model="vm.saturn"
					:type="'type' in $props ? type : 'text'"
					:placeholder="showPlaceholder ? placeholder : ''"
					:readonly="readonly"
					:disabled="disabled"
					:maxlength="maxlength"
					:minlength="minlength"
					:pattern="pattern"
					:autocomplete="autocomplete"
					ref="inputHandle"
					@blur="onBlurHandler"
					@input="onInputHandler"
					@focus="onFocusInput"
					@change="onChangeHandler"
					@click="onClickHandler"
					@keyup.enter="enterHandler"
					@keydown="keydownHandler"
				/>
				<textarea
					v-else
					ref="inputHandle"
					v-model="vm.saturn"
					:placeholder="showPlaceholder ? placeholder : ''"
					:readonly="readonly"
					:disabled="disabled"
					:maxlength="maxlength"
					:minlength="minlength"
					:cols="cols"
					:rows="rows"
					:autocomplete="autocomplete"
					@blur="onBlurHandler"
					@input="onInputHandler"
					@focus="onFocusInput"
					@click="onClickHandler"
					@change="onChangeHandler"
				/>

				<button
					v-if="clearable && valueHasLength && (alwaysClearable || isChangeable)"
					@click="clear"
					:class="getClearButtonClass"
				>
					<i class="fas fa-times-circle" />
				</button>

				<div
					v-if="isADropdownInput"
					class="saturn-icon"
				>
					<i class="fas fa-sort-down" />
				</div>
				<div
					v-else-if="util.hasKey($slots, 'action')"
					class="action-slot"
				>
					<slot name="action"></slot>
				</div>
				<div
					v-else-if="showIcon && !iconLeft"
					class="saturn-icon"
				>
					<i :class="iconStyle" />
				</div>
				<fieldset class="floatingLabel">
					<legend
						class="label label-with-infoicon-containter"
						ref="labelDiv"
					>
						<span v-if="label">
							{{ label === '_' ? '\u00A0' : label }}
						</span>
					</legend>
				</fieldset>
			</div>

			<div
				v-if="util.hasKey($props, 'cdk')"
				v-show="vstore.showCDK"
				class="cdk"
			>
				<div class="cdk-textbox">
					<span>{{ vm.cdk }}</span>
				</div>
				<div class="cdk-icon">
					<span
						v-if="!valuesMatch"
						hoverMessage="Values don't match."
						class="showMessage exclamation"
					>
						<i class="fas fa-exclamation-circle"></i>
					</span>
					<div
						v-if="!valuesMatch"
						class="clickable showMessage copyCDK"
						hoverMessage="Copy CDK's value."
						@click="copyValueFromCDK()"
					>
						<span class="cdk-paste copy">
							<i class="fas fa-paste"></i>
						</span>
						<span class="text">CDK</span>
					</div>
					<span
						v-else
						class="text"
						>CDK</span
					>
				</div>
			</div>

			<div class="saturn-textbox-error-message">
				<slot
					v-if="util.hasKey($slots, 'validation')"
					name="validation"
				></slot>
				<span v-else>Invalid Input</span>
			</div>
		</div>
	</fieldset>
</template>

<script>
	import InfoIcon from '@core/components/InfoIcon.vue';
	import util from '@core/services/util';
	import vstore from '@core/services/vstore';

	export default {
		name: 'InputTextbox',
		inject: {
			wizardLocks: {
				// Provided by FIMenuWizardContent
				from: 'fiMenuFieldLocks',
				default: () => null,
			},
		},
		props: {
			saturn: {},
			cdk: String,
			label: String,
			placeholder: String,
			invalid: [String, Boolean],
			readonly: [String, Boolean],
			disabled: {},
			multiline: {
				type: Boolean,
				default: false,
			},
			cssClass: String,
			copy: Function,
			comparer: Function,
			get: Function,
			set: Function,
			maxlength: {},
			minlength: {},
			type: String,
			pattern: String,
			showIcon: {
				type: Boolean,
				default: true,
			},
			icon: String,
			isUpperCase: Boolean,
			nameValidation: Boolean,
			cols: Number,
			rows: {
				default: 4,
			},
			infoIconObj: Object,
			height: Number,
			width: Number,
			clearable: {
				type: Boolean,
				default: false,
			},
			dropdown: {
				type: Boolean,
				default: false,
			},
			alwaysClearable: {
				type: Boolean,
				default: false,
			},
			dropdownFocused: {
				type: Boolean,
				default: false,
			},
			iconLeft: {},
			nothingSelected: {
				type: String,
				default: '',
			},
			ignoreValidLength: {
				type: Boolean,
				default: false,
			},
			autocomplete: {
				type: String, // one-time-code would make the input OTP and would not autocomplete username and password.
				default: 'on',
			},
			disabledTyping: {
				type: Boolean,
				default: false,
			},
		},
		emits: ['change', 'update:saturn', 'input', 'blur', 'focus', 'keyEnter', 'click', 'cleared'],
		data() {
			return {
				vstore: vstore.state,
				vm: {
					saturn: null,
					cdk: null,
				},
				componentRef: null,
				iconStyle: null,
				previousValue: null,
				focused: false,
				showPlaceholder: false,
				disabledByWizard: false,
			};
		},
		watch: {
			wizardLocks: {
				handler: function () {
					this.disabledByWizard = this.isDisabledByWizard();
				},
				deep: true,
			},
			saturn: {
				handler: function (newValue) {
					this.init();
				},
				deep: true,
				immediate: true,
			},
			dropdownFocused: {
				handler: function (newValue) {
					this.focused = newValue;
				},
			},
		},
		computed: {
			getDimensionStyles() {
				let styles = {};
				if (this.height) {
					styles.maxHeight = `${this.height}px`;
				}
				if (this.width) {
					styles.maxWidth = `${this.width}px`;
				}
				if (this.multiline && this.rows) {
					styles.maxHeight = 'unset';
					styles.height = `calc(${Number(this.rows) * 1.2}em + 30px)`;
				}
				return styles;
			},
			getClearButtonClass() {
				let className = 'clear-input';
				if (!this.showIcon) className += ' noIcon-padding';

				return className;
			},
			isADropdownInput() {
				return this.dropdown && util.hasKey(this.$slots, 'dropdown');
			},
			valueHasLength() {
				return this.ignoreValidLength ? true : this.vm.saturn?.length;
			},
			getCustomCSS() {
				return this.cssClass ? `${this.cssClass} ` : '';
			},
			isChangeable() {
				return !this.readonly && !this.disabled;
			},
			valuesMatch() {
				if (this.comparer) return util.hasKey(this.$props, 'cdk') && this.comparer(this.vm.saturn, this.vm.cdk);
				else return util.hasKey(this.$props, 'cdk') && this.isNull(this.vm.saturn) === this.isNull(this.vm.cdk);
			},
			util() {
				return util;
			},
		},
		created() {
			this.init();
			this.iconStyle = this.multiline && !util.isNull(this.multiline) ? 'fas fa-paragraph' : this.icon && !util.isNull(this.icon) ? this.icon : 'fas fa-font';
		},
		mounted() {
			this.componentRef = this.$refs.container;
			this.disabledByWizard = this.isDisabledByWizard();
		},
		methods: {
			clear() {
				this.vm.saturn = null;
				this.$emit('cleared');
				this.$emit('update:saturn', this.vm.saturn);
				this.animateLabel();
			},
			isLabelFloating() {
				return !this.componentRef?.classList?.contains('empty');
			},
			isDisabledByWizard() {
				if (!this.wizardLocks || !this.$refs.container) return false;
				return this.wizardLocks.isDisabled(this.$refs.container);
			},
			onClickHandler() {
				this.$emit('click');
			},
			generateClassName() {
				let className = 'input-component';
				if (this.dropdown) className += ' dropdown';
				if (this.valuesDontMatch) className += ' no-match';
				if (!this.showIcon) className += ' not-icon';
				if (util.hasKey(this.$slots, 'action')) className += ' action';
				if (this.invalid) className += ' invalid';
				if (this.valuesMatch) className += ' match';
				if (this.multiline) className += ' multi-line';
				if (this.clearable) className += ' clearable';
				if (this.iconLeft) className += ' left-icon';

				if (!this.vm.saturn && this.vm.saturn !== false && this.vm.saturn !== 0 && !this.focused && this.isChangeable && !this.nothingSelected)
					return className + ' empty';

				if (this.focused) return className + ' focused';
				return className;
			},
			valuesDontMatch() {
				if (this.comparer) return util.hasKey(this.$props, 'cdk') && !this.comparer(this.vm.saturn, this.vm.cdk);
				else return util.hasKey(this.$props, 'cdk') && this.isNull(this.vm.saturn) !== this.isNull(this.vm.cdk);
			},
			enterHandler() {
				this.$emit('keyEnter');
			},
			onInputHandler(e) {
				const actualValue = e.target.value.length ? this.vm.saturn : null;
				if (!this.get) {
					this.$emit('update:saturn', actualValue);
				}

				this.$emit('input', actualValue);
				this.$emit('change', actualValue);
			},
			keydownHandler(e) {
				if (this.disabledTyping) {
					e.preventDefault();
				}
			},
			onChangeHandler(e) {
				//this.$emit("update:saturn", this.vm.saturn);
				// this.$emit("change", this.vm.saturn);
			},
			isNull(val) {
				if (val === null) {
					return '';
				} else {
					return val;
				}
			},
			copyValueFromCDK() {
				if (this.copy) {
					this.vm.saturn = this.copy(this.vm.cdk);
				} else {
					this.vm.saturn = this.vm.cdk;
				}

				this.updateSaturnGetterVersion();
			},
			init() {
				this.vm.saturn = this.set ? this.set(this.saturn) : this.saturn;
				this.previousValue = this.vm.saturn;
				this.vm.cdk = this.cdk;
			},
			onBlurHandler($e) {
				this.focused = false;
				this.vm.saturn = this.vm.saturn?.toString().trim();
				this.updateSaturnGetterVersion();
				this.$emit('blur', $e);

				this.animateLabel();
			},
			onFocusInput() {
				this.focused = true;
				this.$emit('focus');
				this.animateLabel();
			},
			animateLabel() {
				if (!this.vm.saturn && this.vm.saturn !== false && this.vm.saturn !== 0 && this.isLabelFloating() && this.isChangeable && !this.nothingSelected) {
					this.componentRef?.classList.add('empty');
					this.showPlaceholder = false;
				} else if (!this.isLabelFloating()) {
					this.componentRef?.classList.remove('empty');
					this.showPlaceholder = true;
				}
			},
			updateSaturnGetterVersion() {
				if (this.get) {
					this.$emit('update:saturn', this.get(this.isUpperCase ? this.vm.saturn.toUpperCase : this.vm.saturn));
					this.$emit('change');
				}
			},
		},
		components: {
			InfoIcon,
		},
	};

	/*
             * USAGE
             *

             <InputTextbox label="Customer" v-model:saturn="customer1.saturn" :cdk="customer1.cdk" :invalid="v$.customer1.saturn.$invalid" placeholder="Name">
                <template #validation>
                    <div v-if="v$.customer1.saturn.required.$invalid">Customer is required</div>
                </template>
            </InputTextbox>

             <InputTextbox label="Customer" v-model:saturn="customer1.saturn" placeholder="Name" :invalid="v$.customer1.saturn.$invalid" readonly="true"></InputTextbox>
             <InputTextbox label="Customer" v-model:saturn="customer1.saturn" placeholder="Name" :invalid="v$.customer1.saturn.$invalid"></InputTextbox>
            */
</script>
<style></style>
